import { useTranscription } from "@context/TranscriptionContext";
import {
  faMicrophone,
  faTriangleExclamation,
  faVial,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn, Clipboard } from "@scribenote/brian-design-system";
import { useLabRoomStore } from "@transcription/dev/labRoomStore";
import { devToolsEnabled } from "@utils/devTools";
import { useState } from "react";

/**
 * The single Talk-to-Brian control: a header icon button that starts/stops live
 * transcription. Stored audio anchors to the clinic; patient/visit attribution is
 * the agent's, from the conversation itself.
 *
 * With no text label the icon carries the whole session state: idle mic,
 * connecting pulse, steady indigo live, red warning triangle on failure. The
 * tooltip repeats it in words for anyone who needs more than color alone.
 *
 * The button lives on the desk wherever the control cluster does, so it has one
 * resting treatment (paper ink, faint paper tint on hover). Live overrides the
 * ink with `accent-lite`: recording is Brian listening, an AI act, so it takes
 * the indigo accent rather than a status color. Failure alone keeps
 * `ink-stopped`, the system's real red: a genuine error has to win over the
 * backdrop.
 *
 * The expanding ring is deliberately NOT the recording indicator: it tracks
 * `audioDetected`, so it answers "did it hear me" rather than "is it on". Until
 * then the mic holds dimmed indigo, the same window in which `noAudioWarning`
 * counts down. Being a latch, the ring keeps pulsing for the rest of the
 * session, reporting "audio got through" rather than "audio is getting
 * through now".
 */
export const TalkToBrianButton = () => {
  const { status, error, start, stop, noAudioWarning, audioDetected } =
    useTranscription();
  // Dev-only lab override (DevInjectPanel): when set, the mic streams as
  // that room's ambient device, so the tooltip says so.
  const labRoom = useLabRoomStore((s) => s.room);
  const labRoomName = devToolsEnabled() ? labRoom?.name : undefined;

  const isConnecting = status === "connecting" || status === "reconnecting";
  const isLive = status === "recording" || status === "stopping";
  /** Live AND the transcriber has confirmed a turn, so the pulse means "I can hear you". */
  const isHearing = isLive && audioDetected;
  /** A session is in progress, so a click stops it rather than starting one. */
  const isActive = isConnecting || isLive;
  const isError = status === "error";
  const busy = status === "connecting" || status === "stopping";

  const [noAudioDismissed, setNoAudioDismissed] = useState(false);
  const [errorDismissed, setErrorDismissed] = useState(false);

  const noAudioOpen = isActive && noAudioWarning && !noAudioDismissed;
  const errorOpen = isError && !errorDismissed;

  const tooltip =
    status === "connecting"
      ? "Connecting…"
      : status === "reconnecting"
        ? "Reconnecting…"
        : status === "recording"
          ? "Click to stop"
          : status === "stopping"
            ? "Stopping…"
            : isError
              ? "Recording failed — click to retry"
              : "Talk to Brian";

  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (!isActive) {
            setNoAudioDismissed(false);
            setErrorDismissed(false);
          }
          void (isActive ? stop : start)();
        }}
        disabled={busy}
        aria-pressed={isActive}
        // One name for both directions: `aria-pressed` already carries whether
        // this started or stopped, and the tooltip it would otherwise fall back
        // to interpolates a lab room name.
        data-dd-action-name="Talk to Brian"
        aria-label={
          isActive
            ? "Stop talking to Brian"
            : isError
              ? "Talk to Brian — recording failed, click to retry"
              : "Talk to Brian"
        }
        title={
          labRoomName
            ? `${tooltip} (lab: the mic streams as ${labRoomName}'s ambient device)`
            : tooltip
        }
        data-testid="talk-to-brian"
        className={cn(
          "relative flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full",
          "transition-colors duration-150 ease-paper motion-reduce:transition-none",
          "hover:bg-paper/10 disabled:cursor-default",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-lite",
          isError
            ? "text-ink-stopped"
            : isLive
              ? "text-accent-lite"
              : "text-ink-inverse",
        )}
      >
        {/*
         * Recording is a steady indigo mic; the pulse is reserved for audio
         * actually arriving, so the ring reads as "I can hear you" rather than
         * just "on". A ring rather than a filled disc, so the indigo glyph stays
         * legible at every frame of the animation. Inset so the 2x scale lands
         * just outside the 40px button; `pointer-events-none` because that
         * overhang covers the neighbouring icon, and as a descendant of this
         * button it would otherwise swallow clicks meant for it.
         * `animate-mic-pulse` (index.css) is a half-rate `animate-ping` that
         * holds still under reduced motion.
         */}
        {isHearing && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-2 animate-mic-pulse rounded-full border-2 border-accent-lite"
          />
        )}
        {/* `opacity-75` dims the whole glyph rather than its ink: live-but-unheard
            is the one state read as a weaker version of the same indigo. */}
        <FontAwesomeIcon
          icon={isError ? faTriangleExclamation : faMicrophone}
          className={cn(
            "relative text-lg",
            isConnecting && "animate-pulse motion-reduce:animate-none",
            isLive && !isHearing && "opacity-75",
          )}
        />
        {labRoomName && (
          <span
            data-testid="talk-to-brian-lab-room"
            aria-hidden="true"
            className="absolute -bottom-0.5 -right-0.5 text-[10px] text-ink-flag"
          >
            <FontAwesomeIcon icon={faVial} />
          </span>
        )}
      </button>
      {/* Both clipboards are shown by the session, not by a click, so they stay
          mounted and closed. The test handle rides the title, which is the only
          child prop the Clipboard takes, and is dropped while closed so it never
          reports a dialog that isn't on screen. */}
      <Clipboard
        open={noAudioOpen}
        onClose={() => setNoAudioDismissed(true)}
        size="sm"
        dismissable
        title={
          <span data-testid={noAudioOpen ? "no-audio-modal" : undefined}>
            No audio detected
          </span>
        }
      >
        <p className="text-sm">
          I&rsquo;m not hearing anything yet. Check that your mic is on and
          unmuted.
        </p>
      </Clipboard>
      <Clipboard
        open={errorOpen}
        onClose={() => setErrorDismissed(true)}
        size="sm"
        dismissable
        title={
          <span data-testid={errorOpen ? "mic-error-modal" : undefined}>
            Microphone error
          </span>
        }
      >
        <p className="text-sm">{error ?? "Recording failed."}</p>
      </Clipboard>
    </>
  );
};
